'use client';

import { useState } from 'react';
import UIButton from 'components/buttons/UIButton';
import { MedicareApiPlan } from 'types/api';
import EnrollmentHandler from '../EnrollmentHandler';
import FormError from '../forms/FormError';
import styles from '../PlanCard.module.scss';
import { PlanCardContext } from './planCardTypes';
import DeleteIcon from 'components/icons/DeleteIcon';
import DisabledTooltip from './DisabledTooltip';
import AddToCartButton from './AddToCartButton';

interface PlanActionsProps {
  plan: MedicareApiPlan;
  context: PlanCardContext;
  detailsPageUrl: string;
  isInCart: boolean;
  isSavedPlansContext: boolean;
  isInRecommendedTab?: boolean;
  onAddToCart: () => void;
  onDelete: () => void;
  isPending?: boolean;
  enrollmentDisabled?: boolean;
  isInComparison?: boolean;
  isComparisonFull?: boolean;
  onCompareToggle?: () => void;
}

export default function PlanActions({
  plan,
  context,
  detailsPageUrl,
  isInCart,
  isSavedPlansContext,
  isInRecommendedTab = false,
  onAddToCart,
  onDelete,
  isPending = false,
  enrollmentDisabled = false,
  isInComparison = false,
  isComparisonFull = false,
  onCompareToggle,
}: PlanActionsProps) {
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);
  const isBrowseContext = context === 'browse';

  // Browse page
  if (isBrowseContext) {
    const compareDisabled = !isInComparison && isComparisonFull;

    return (
      <div className={styles.footer}>
        <div className={styles.reviewAndCart}>
          <UIButton
            buttonType="secondary"
            label="Review full plan details"
            href={detailsPageUrl}
            fullWidth={true}
          />
          <AddToCartButton
            isInCart={isInCart}
            onAddToCart={onAddToCart}
            fullWidth={true}
            isPending={isPending}
          />
        </div>
        <div
          className={`${styles.compareWrapper} ${
            compareDisabled ? styles.compareDisabled : ''
          }`}
        >
          <label
            className={`${styles.compareLabel} ${
              compareDisabled ? styles.compareDisabled : ''
            }`}
          >
            <input
              type="checkbox"
              className={styles.compareCheckbox}
              checked={isInComparison}
              aria-disabled={compareDisabled}
              aria-describedby={
                compareDisabled ? `compare-tooltip-${plan.id}` : undefined
              }
              onChange={() => {
                if (!compareDisabled) onCompareToggle?.();
              }}
            />
            Add to compare
          </label>
          {compareDisabled && (
            <DisabledTooltip
              id={`compare-tooltip-${plan.id}`}
              message="You can only compare 2 plans at a time."
            />
          )}
        </div>
      </div>
    );
  }

  // Cart and Saved pages
  return (
    <div className={styles.planActions}>
      {!isInRecommendedTab && (
        <div className={styles.planDeleteAction}>
          <UIButton
            buttonType="text"
            label={isPending ? 'Deleting...' : 'Delete'}
            icon={<DeleteIcon />}
            iconPosition="left"
            onClick={onDelete}
            disabled={isPending}
            fullWidth={true}
          />
        </div>
      )}
      <div className={styles.planMainActions}>
        <UIButton
          buttonType="secondary"
          label="Review full plan details"
          href={detailsPageUrl}
          fullWidth={true}
        />
        {isSavedPlansContext ? (
          <AddToCartButton
            isInCart={isInCart}
            onAddToCart={onAddToCart}
            fullWidth={true}
            isPending={isPending}
          />
        ) : (
          <EnrollmentHandler
            plan={plan}
            label="Enroll in this plan"
            onError={setEnrollmentError}
            disabled={enrollmentDisabled}
          />
        )}
      </div>
      {enrollmentError && <FormError error={enrollmentError} />}
    </div>
  );
}
